import { useState, useEffect, useRef } from 'react';
import { Zap, Copy, Check, ExternalLink, Sparkle, Sparkles, Star, Rocket, ArrowLeft, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  DrawerClose,
} from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { useToast } from '@/hooks/useToast';
import { useZaps } from '@/hooks/useZaps';
import { useWallet } from '@/hooks/useWallet';
import { useIsMobile } from '@/hooks/useIsMobile';
import type { Event } from 'nostr-tools';
import QRCode from 'qrcode';
import type { WebLNProvider } from "@webbtc/webln-types";
import { mobileDrawerContentStyle, MOBILE_DRAWER_OVERLAY_CLASS } from '@/lib/drawerStyles';

interface ZapDialogProps {
  target: Event;
  children?: React.ReactNode;
  className?: string;
}

const presetAmounts = [
  { amount: 1, icon: Sparkle },
  { amount: 50, icon: Sparkles },
  { amount: 100, icon: Zap },
  { amount: 250, icon: Star },
  { amount: 1000, icon: Rocket },
];

/* ── Amount selection + comment form ──────────────────────────── */
interface ZapFormProps {
  amount: number | string;
  comment: string;
  isZapping: boolean;
  setAmount: (a: number | string) => void;
  setComment: (c: string) => void;
  onSubmit: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

function ZapForm({ amount, comment, isZapping, setAmount, setComment, onSubmit, inputRef }: ZapFormProps) {
  return (
    <div className="flex flex-col gap-3 pb-2">
      <ToggleGroup
        type="single"
        value={String(amount)}
        onValueChange={(value) => { if (value) setAmount(parseInt(value, 10)); }}
        className="grid grid-cols-5 gap-1 w-full"
      >
        {presetAmounts.map(({ amount: preset, icon: Icon }) => (
          <ToggleGroupItem
            key={preset}
            value={String(preset)}
            className="flex flex-col h-auto min-w-0 text-xs px-1 py-2"
          >
            <Icon className="h-4 w-4 mb-1" />
            <span className="truncate">{preset}</span>
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      <div className="flex items-center gap-2">
        <div className="h-px flex-1 bg-muted" />
        <span className="text-xs text-muted-foreground">OR</span>
        <div className="h-px flex-1 bg-muted" />
      </div>

      <Input
        ref={inputRef}
        id="custom-amount"
        type="number"
        placeholder="Custom amount (sats)"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        className="w-full"
        min={1}
      />

      <Textarea
        id="zap-comment"
        placeholder="Add a comment (optional)"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        className="w-full resize-none"
        rows={2}
      />

      <Button
        onClick={onSubmit}
        className="w-full"
        disabled={isZapping || !amount || Number(amount) <= 0}
        size="lg"
      >
        {isZapping ? (
          <span className="flex items-center gap-2">
            <span className="h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
            Creating invoice…
          </span>
        ) : (
          <>
            <Zap className="h-4 w-4 mr-2" />
            Zap {amount} sats
          </>
        )}
      </Button>
    </div>
  );
}

/* ── Invoice / QR view ─────────────────────────────────────────── */
interface InvoiceViewProps {
  invoice: string;
  amount: number | string;
  comment: string;
  isZapping: boolean;
  qrCodeUrl: string;
  copied: boolean;
  webln: WebLNProvider | null;
  onCopy: () => void;
  onOpenWallet: () => void;
  onWebLNPay: () => void;
}

function InvoiceView({
  invoice, amount, isZapping, qrCodeUrl, copied, webln,
  onCopy, onOpenWallet, onWebLNPay,
}: InvoiceViewProps) {
  return (
    <div className="flex flex-col gap-3 pb-2">
      <div className="text-center">
        <div className="text-2xl font-bold">{amount} sats</div>
        <div className="text-xs text-muted-foreground mt-0.5">
          Scan or copy to pay with any Lightning wallet
        </div>
      </div>

      <Separator />

      {/* QR Code — capped so it never dominates a small screen */}
      <div className="flex justify-center">
        <Card className="p-2 w-full max-w-[200px] sm:max-w-[240px]">
          <CardContent className="p-0 flex justify-center">
            {qrCodeUrl ? (
              <img
                src={qrCodeUrl}
                alt="Lightning Invoice QR Code"
                className="w-full h-auto aspect-square object-contain rounded"
              />
            ) : (
              <div className="w-full aspect-square bg-muted animate-pulse rounded" />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Invoice string */}
      <div className="space-y-1">
        <Label htmlFor="invoice-display" className="text-xs">Lightning Invoice</Label>
        <div className="flex gap-2 min-w-0">
          <Input
            id="invoice-display"
            value={invoice}
            readOnly
            className="font-mono text-xs min-w-0 flex-1 truncate"
            onClick={(e) => e.currentTarget.select()}
          />
          <Button
            variant="outline"
            size="icon"
            onClick={onCopy}
            className="shrink-0"
            aria-label="Copy invoice"
          >
            {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Payment actions */}
      <div className="flex flex-col gap-2 pt-1">
        {webln && (
          <Button onClick={onWebLNPay} disabled={isZapping} className="w-full" size="lg">
            <Zap className="h-4 w-4 mr-2" />
            {isZapping ? 'Processing…' : 'Pay with WebLN'}
          </Button>
        )}
        <Button variant="outline" onClick={onOpenWallet} className="w-full" size="lg">
          <ExternalLink className="h-4 w-4 mr-2" />
          Open in Lightning Wallet
        </Button>
      </div>
    </div>
  );
}

/* ── Main ZapDialog ────────────────────────────────────────────── */
export function ZapDialog({ target, children, className }: ZapDialogProps) {
  const [open, setOpen] = useState(false);
  const { user } = useCurrentUser();
  const { data: author } = useAuthor(target.pubkey);
  const { toast } = useToast();
  const { webln, activeNWC } = useWallet();
  const { zap, isZapping, invoice, setInvoice } = useZaps(target, webln, activeNWC, () => setOpen(false));

  const [amount, setAmount] = useState<number | string>(100);
  const [comment, setComment] = useState('');
  const [copied, setCopied] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  // Reset comment to meaningful default when target changes
  useEffect(() => {
    if (target) setComment('');
  }, [target]);

  // Generate QR code whenever invoice changes
  useEffect(() => {
    if (!invoice) { setQrCodeUrl(''); return; }
    let cancelled = false;
    QRCode.toDataURL(invoice.toUpperCase(), {
      width: 512, margin: 2,
      color: { dark: '#000000', light: '#FFFFFF' },
    }).then(url => { if (!cancelled) setQrCodeUrl(url); })
      .catch(err => { if (!cancelled) console.error('QR generation failed:', err); });
    return () => { cancelled = true; };
  }, [invoice]);

  // Reset all state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setAmount(100);
      setComment('');
      setInvoice(null);
      setCopied(false);
      setQrCodeUrl('');
    }
  }, [open, setInvoice]);

  const handleZap = () => {
    const finalAmount = typeof amount === 'string' ? parseInt(amount, 10) : amount;
    if (!finalAmount || finalAmount <= 0) {
      toast({ title: 'Invalid amount', description: 'Please enter a valid amount in sats.', variant: 'destructive' });
      return;
    }
    zap(finalAmount, comment);
  };

  const handleCopy = async () => {
    if (!invoice) return;
    try {
      await navigator.clipboard.writeText(invoice);
      setCopied(true);
      toast({ title: 'Invoice copied', description: 'Lightning invoice copied to clipboard.' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Copy failed', description: 'Could not copy to clipboard.', variant: 'destructive' });
    }
  };

  const openInWallet = () => {
    if (invoice) window.open(`lightning:${invoice}`, '_blank');
  };

  const handleWebLNPay = () => {
    const finalAmount = typeof amount === 'string' ? parseInt(amount, 10) : amount;
    zap(finalAmount, comment);
  };

  const resetInvoice = () => {
    setInvoice(null);
    setQrCodeUrl('');
  };

  const handleClose = (newOpen: boolean) => {
    if (!newOpen) {
      resetInvoice();
    }
    setOpen(newOpen);
  };

  // Don't render if: not logged in, viewing own content, or author has no lightning address
  if (!user || user.pubkey === target.pubkey) return null;
  if (!author?.metadata?.lud06 && !author?.metadata?.lud16) return null;

  const title = invoice ? 'Lightning Payment' : 'Send a Zap ⚡';
  const description = invoice
    ? 'Scan the QR code or copy the invoice to pay with any Lightning wallet.'
    : 'Send a Bitcoin Lightning tip to support this creator.';

  /* ── Mobile: Vaul bottom sheet ── */
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={handleClose} dismissible>
        <DrawerTrigger asChild>
          <div className={cn('cursor-pointer', className)}>{children}</div>
        </DrawerTrigger>
        <DrawerContent
          className="flex flex-col"
          overlayClassName={MOBILE_DRAWER_OVERLAY_CLASS}
          style={mobileDrawerContentStyle}
        >
          {/* Header — drag handle is auto-rendered above this by DrawerContent */}
          <DrawerHeader className="relative shrink-0 px-4 pt-2 pb-3 text-left">
            {/* Back / Close row */}
            <div className="flex items-center justify-between mb-1">
              <div className="w-8">
                {invoice && (
                  <button
                    onClick={resetInvoice}
                    className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-muted transition-colors"
                    aria-label="Back to amount"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                )}
              </div>
              <DrawerTitle className="text-base font-semibold flex-1 text-center">
                {title}
              </DrawerTitle>
              <DrawerClose asChild>
                <button
                  className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-muted transition-colors"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </DrawerClose>
            </div>
            <DrawerDescription className="text-xs text-center px-2">
              {description}
            </DrawerDescription>
          </DrawerHeader>

          {/* Scrollable content area — safe-area bottom padding */}
          <div
            className="flex-1 overflow-y-auto overscroll-contain px-4"
            style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
          >
            {invoice ? (
              <InvoiceView
                invoice={invoice}
                amount={amount}
                comment={comment}
                isZapping={isZapping}
                qrCodeUrl={qrCodeUrl}
                copied={copied}
                webln={webln}
                onCopy={handleCopy}
                onOpenWallet={openInWallet}
                onWebLNPay={handleWebLNPay}
              />
            ) : (
              <ZapForm
                amount={amount}
                comment={comment}
                isZapping={isZapping}
                setAmount={setAmount}
                setComment={setComment}
                onSubmit={handleZap}
                inputRef={inputRef}
              />
            )}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  /* ── Desktop: Dialog ── */
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <div className={cn('cursor-pointer', className)}>{children}</div>
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-[440px] max-h-[90dvh] flex flex-col overflow-hidden"
        data-testid="zap-modal"
      >
        <DialogHeader className="shrink-0">
          {invoice && (
            <button
              onClick={resetInvoice}
              className="absolute left-4 top-4 p-1 rounded-full text-muted-foreground hover:text-foreground"
              aria-label="Back to amount"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-2">
          {invoice ? (
            <InvoiceView
              invoice={invoice}
              amount={amount}
              comment={comment}
              isZapping={isZapping}
              qrCodeUrl={qrCodeUrl}
              copied={copied}
              webln={webln}
              onCopy={handleCopy}
              onOpenWallet={openInWallet}
              onWebLNPay={handleWebLNPay}
            />
          ) : (
            <ZapForm
              amount={amount}
              comment={comment}
              isZapping={isZapping}
              setAmount={setAmount}
              setComment={setComment}
              onSubmit={handleZap}
              inputRef={inputRef}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
